/**
 * The arithmetic behind one call's chart, kept out of the component that draws
 * it — the same split galleryChart.ts makes, and for the same reason: a scale
 * that rounds badly and a line that quietly bridges a gap in the data are both
 * bugs you can only catch by reading numbers.
 *
 * The one rule worth stating out loud is that gap. A sample whose value is null
 * is «this side could not measure it», which is a different fact from zero and
 * from «the call stopped» — a connection that answered nothing for ten seconds
 * must show a hole there, not a straight line across it. So a series is drawn as
 * several path segments, and every null ends one.
 */

import type { AdminCallSample } from '../api/client.ts';

/** Which question the chart is answering. One at a time; they share no scale. */
export type CallMetric = 'bitrate' | 'voice' | 'rtt' | 'loss' | 'fps' | 'headroom';

export const CALL_METRIC_LABEL: Record<CallMetric, string> = {
  bitrate: 'Битрейт',
  voice: 'Громкость',
  rtt: 'Задержка',
  loss: 'Потери',
  fps: 'Кадры и размер',
  headroom: 'Оценка канала',
};

export interface ChartSeries {
  key: string;
  label: string;
  /** One value per sample, in the sample's own order. Null is a hole. */
  values: (number | null)[];
}

/** A point on the plot, in viewBox units. */
export interface ChartBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export function seriesFor(metric: CallMetric, samples: readonly AdminCallSample[]): ChartSeries[] {
  switch (metric) {
    case 'bitrate':
      return [
        { key: 'videoOut', label: 'видео ↑', values: samples.map((s) => s.videoOutBps) },
        { key: 'videoIn', label: 'видео ↓', values: samples.map((s) => s.videoInBps) },
        { key: 'audioOut', label: 'звук ↑', values: samples.map((s) => s.audioOutBps) },
        { key: 'audioIn', label: 'звук ↓', values: samples.map((s) => s.audioInBps) },
      ];
    case 'voice':
      // Percent of full scale rather than the stored 0..1, for the same reason
      // the loss panel converts: an axis reading «0.02» is one nobody scales in
      // their head. The two lines together are the diagnosis — «↑ низкая» is a
      // microphone this device is suppressing, «↓ низкая» with the far end's ↑
      // high is a call that arrived loud and played quietly.
      return [
        {
          key: 'voiceOut',
          label: 'голос ↑, %',
          values: samples.map((s) => (s.audioOutLevel === null ? null : s.audioOutLevel * 100)),
        },
        {
          key: 'voiceIn',
          label: 'голос ↓, %',
          values: samples.map((s) => (s.audioInLevel === null ? null : s.audioInLevel * 100)),
        },
      ];
    case 'rtt':
      return [
        { key: 'rtt', label: 'rtt', values: samples.map((s) => s.rttMs) },
        { key: 'jitter', label: 'джиттер', values: samples.map((s) => s.jitterMs) },
      ];
    case 'loss':
      return [
        // Percent rather than the stored fraction: a chart axis reading «0.04»
        // is a chart nobody converts in their head correctly at a glance.
        { key: 'loss', label: 'потери, %', values: samples.map((s) => (s.loss === null ? null : s.loss * 100)) },
        {
          key: 'limited',
          label: 'кодировщик в лимите, %',
          values: samples.map((s) => (s.limitedShare === null ? null : s.limitedShare * 100)),
        },
      ];
    case 'fps':
      return [
        { key: 'fpsOut', label: 'к/с ↑', values: samples.map((s) => s.fpsOut) },
        { key: 'fpsIn', label: 'к/с ↓', values: samples.map((s) => s.fpsIn) },
        { key: 'heightOut', label: 'высота кадра ↑', values: samples.map((s) => s.heightOut) },
        { key: 'heightIn', label: 'высота кадра ↓', values: samples.map((s) => s.heightIn) },
      ];
    case 'headroom':
      return [
        // The pair that answers «мал ли потолок»: what the congestion
        // controller thought the path would carry, against what was actually
        // sent. A gap between them that stays open is a cap set too low.
        { key: 'available', label: 'канал держит', values: samples.map((s) => s.availableOutBps) },
        { key: 'videoOut', label: 'видео ↑', values: samples.map((s) => s.videoOutBps) },
      ];
  }
}

/** 1, 2 or 5 times a power of ten, at or above the largest value. */
export function niceTop(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (value <= step * power) return step * power;
  }
  return 10 * power;
}

/** The scale for a whole panel: every series in it shares one axis. */
export function topOf(series: readonly ChartSeries[]): number {
  let max = 0;
  for (const line of series) {
    for (const value of line.values) {
      if (value !== null && value > max) max = value;
    }
  }
  return niceTop(max);
}

/**
 * One series as SVG path data.
 *
 * `M` starts a new segment, which is what makes a hole a hole: a run of nulls
 * ends the current line and the next number starts a fresh one. A single point
 * between two holes is drawn as a zero-length segment so that it is still
 * visible with a round line cap — a call that reported once and went quiet
 * should not be an empty panel.
 */
export function linePath(
  values: readonly (number | null)[],
  top: number,
  box: ChartBox,
): string {
  if (values.length === 0) return '';
  const span = Math.max(1, values.length - 1);
  const height = box.bottom - box.top;
  const width = box.right - box.left;
  let path = '';
  let open = false;

  values.forEach((value, index) => {
    if (value === null) {
      open = false;
      return;
    }
    const x = box.left + (width * index) / span;
    const y = box.bottom - (height * Math.min(value, top)) / top;
    if (!open) {
      path += `${path ? ' ' : ''}M${round(x)} ${round(y)}`;
      open = true;
      // A lone point still has to be drawn, so it gets a segment of no length.
      const next = values[index + 1];
      if (next === null || next === undefined) path += ` L${round(x)} ${round(y)}`;
      return;
    }
    path += ` L${round(x)} ${round(y)}`;
  });

  return path;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** `500 кбит/с`, `38 мс`, `2.4 %` — the axis label for one metric. */
export function formatMetric(metric: CallMetric, value: number): string {
  switch (metric) {
    case 'bitrate':
    case 'headroom':
      return value >= 1_000_000
        ? `${Math.round(value / 100_000) / 10} Мбит/с`
        : `${Math.round(value / 1000)} кбит/с`;
    case 'rtt':
      return `${Math.round(value)} мс`;
    case 'loss':
      return `${Math.round(value * 10) / 10} %`;
    // Two decimals, not one: ordinary speech peaks around 5 % of full scale, so
    // a whole percent is the difference between quiet and inaudible.
    case 'voice':
      return `${Math.round(value * 100) / 100} %`;
    case 'fps':
      return String(Math.round(value));
  }
}

/** `0:35` — how far into the call a sample is. */
export function offsetLabel(at: number, startedAt: number): string {
  const seconds = Math.max(0, Math.round((at - startedAt) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Which samples get a label under them: about one every N, always including the
 * first and the last, so the axis reads as a span rather than as a row of ticks
 * whose ends are missing.
 */
export function labelledIndices(count: number, wanted = 6): number[] {
  if (count <= 0) return [];
  if (count <= wanted) return Array.from({ length: count }, (_, index) => index);
  const step = Math.ceil(count / wanted);
  const indices: number[] = [];
  for (let index = 0; index < count; index += step) indices.push(index);
  if (indices.at(-1) !== count - 1) indices.push(count - 1);
  return indices;
}
